import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  IAccident,
  IAccidentDetails,
  IAccidentForm,
  IAccidentNature,
} from "./types"
import api from "services/api"

export function useAccidents(
  eventId: string,
  memberId: string,
  config?: UseQueryOptions<IAccident[]>
) {
  return useQuery<IAccident[]>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "members",
      memberId,
      "accidents",
    ],
    config
  )
}

export function useCreateAccident(
  eventId: string,
  memberId: string,
  config?: UseMutationOptions<unknown, unknown, IAccidentForm, unknown>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, IAccidentForm, unknown>(
    (data) => {
      return api.post(
        `operation/production/events/execution/${eventId}/members/${memberId}/accidents`,
        data
      )
    },

    {
      ...config,
      onSuccess() {
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
          memberId,
          "accidents",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
        ])
      },
    }
  )
}

export function useAccident(
  eventId: string,
  memberId: string,
  accidentId: string,
  config?: UseQueryOptions<IAccidentDetails>
) {
  return useQuery<IAccidentDetails>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "members",
      memberId,
      "accidents",
      accidentId,
    ],
    {
      ...config,
    }
  )
}

export function useEditAccident(
  eventId: string,
  memberId: string,
  accidentId: string,
  config?: UseMutationOptions<unknown, unknown, IAccidentForm>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, IAccidentForm>(
    ({ ...rest }) =>
      api.post(
        `operation/production/events/execution/${eventId}/members/${memberId}/accidents/${accidentId}`,
        { ...rest }
      ),
    {
      ...config,
      onMutate: async (editedData) => {
        const key = [
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
          memberId,
          "accidents",
          accidentId,
        ]
        await queryClient.cancelQueries(key)
        const previous = queryClient.getQueryData<IAccidentForm>(key)
        queryClient.setQueryData(key, { ...previous, ...editedData })

        return { previous }
      },
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
          memberId,
          "accidents",
        ])
      },
      onError(err, vars, context: any) {
        config?.onError?.(err, vars, context)
        queryClient.setQueryData(
          [
            "operation",
            "production",
            "events",
            "execution",
            eventId,
            "members",
            memberId,
            "accidents",
            accidentId,
          ],
          context?.previous
        )
      },
    }
  )
}

export function useDeleteAccident(
  eventId: string,
  memberId: string,
  accidentId: string,
  config?: UseMutationOptions
) {
  const queryClient = useQueryClient()

  return useMutation(
    () =>
      api.delete(
        `operation/production/events/execution/${eventId}/members/${memberId}/accidents/${accidentId}`
      ),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
          memberId,
          "accidents",
        ])
      },
    }
  )
}

export function useAccidentNatures(
  config?: UseQueryOptions<IAccidentNature[]>
) {
  return useQuery<IAccidentNature[]>(
    ["operation", "production", "events", "external", "accident-nature"],
    {
      ...config,
    }
  )
}
